import styled from '@emotion/styled';
import { Rating } from '@mui/material';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import StyledTextField from '../field/StyledTextField';
import SrOnlyText from '../text/SrOnlyText';
import { LabelSmall } from '../text/Typography';

export interface IRateReviewFieldsProps {
    rating: number;
    onRatingChange: (rating: number) => void;
    comment?: string;
    onCommentChange?: (comment: string) => void;
    /** Drop the comment field when a surface only collects a score. */
    hideComment?: boolean;
    disabled?: boolean;
    className?: string;
}

/**
 * The star input and optional comment field of a review, without any surrounding chrome. Controlled,
 * so the host owns the draft.
 *
 * `RateReviewDialog` wraps this for the "click the rating to review" flow; embed it directly on a
 * surface that is already a dialog (or a form) and so can't nest another one — that's how the
 * org feedback dialog shows the same fields above its comment thread.
 */
export default function RateReviewFields({
    rating,
    onRatingChange,
    comment = '',
    onCommentChange,
    hideComment = false,
    disabled = false,
    className,
}: Readonly<IRateReviewFieldsProps>) {
    const { t } = useTranslation('common');
    const ratingLabelId = useId();
    const commentId = useId();

    return (
        <Wrap className={className}>
            <RatingRow>
                <SrOnlyText id={ratingLabelId}>{t('rating.review.yourRating')}</SrOnlyText>
                <Rating
                    size="large"
                    value={rating}
                    disabled={disabled}
                    aria-labelledby={ratingLabelId}
                    onChange={(_, value) => onRatingChange(value ?? 0)}
                />
            </RatingRow>

            {!hideComment && (
                <CommentSection>
                    <CommentLabel htmlFor={commentId}>
                        {t('rating.review.comment')} <Optional>{t('rating.review.optional')}</Optional>
                    </CommentLabel>
                    <StyledTextField
                        id={commentId}
                        fullWidth
                        multiline
                        minRows={3}
                        value={comment}
                        disabled={disabled}
                        placeholder={t('rating.review.commentPlaceholder')}
                        onChange={(event) => onCommentChange?.(event.target.value)}
                    />
                </CommentSection>
            )}
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
`;

const RatingRow = styled.div`
    display: flex;
    align-items: center;
    margin: 0.75rem 0 1.25rem;
`;

const CommentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const CommentLabel = styled(LabelSmall.withComponent('label'))`
    color: ${(props) => props.theme.color.text};
    font-weight: 700;
`;

const Optional = styled.span`
    font-weight: 400;
    color: ${(props) => props.theme.color.textBody};
`;
